<div class="board">
  <div class="head">
    <div>
      <h2>Profile funnel</h2>
      <p class="sub">
        Every account is created the moment someone signs in, from the name their
        provider gave us — so an abandoned onboarding leaves a profile with nothing
        on it. Those are <b>ghosts</b>: they can use the app, but they are no longer
        shown in anyone's Nearby list or AI matches, because a name is not something
        a stranger can decide to meet you on.
      </p>
    </div>
    <button mat-stroked-button (click)="load()" [disabled]="loading">Refresh</button>
  </div>

  <div class="notice ok" *ngIf="notice">{{ notice }}</div>
  <div class="notice bad" *ngIf="error">{{ error }}</div>

  <div class="loading" *ngIf="loading"><mat-spinner diameter="28"></mat-spinner></div>

  <ng-container *ngIf="!loading && report">
    <div class="stats">
      <div class="stat">
        <div class="n">{{ report.stages.total }}</div>
        <div class="l">Accounts</div>
      </div>
      <div class="stat bad">
        <div class="n">{{ report.stages.ghost }}</div>
        <div class="l">Ghosts</div>
        <div class="hint">{{ percent(report.stages.ghost, report.stages.total) }} of all</div>
      </div>
      <div class="stat">
        <div class="n">{{ report.stages.neverPastEditor }}</div>
        <div class="l">Never got in</div>
        <div class="hint">no in-app nudge reaches them</div>
      </div>
      <div class="stat">
        <div class="n">{{ report.stages.listed }}</div>
        <div class="l">Listed</div>
        <div class="hint">shown to others</div>
      </div>
      <div class="stat good">
        <div class="n">{{ report.stages.top }}</div>
        <div class="l">Top</div>
        <div class="hint">100% complete</div>
      </div>
      <div class="stat">
        <div class="n">{{ report.stages.engaged }}</div>
        <div class="l">Favorited someone</div>
        <div class="hint">{{ percent(report.stages.engaged, report.stages.total) }} of all</div>
      </div>
    </div>

    <h3>Which field is the wall</h3>
    <p class="note">
      Read the middle column first. The profile editor already refuses to save without
      the five mandatory fields, so anyone counted there got through it and stopped
      anyway — they are pointing straight at the field they stopped on. Accounts that
      bounced off the editor are missing everything and blame no field in particular.
    </p>
    <table>
      <thead>
        <tr>
          <th>Field</th>
          <th class="num">Missing after getting in</th>
          <th class="num">Missing among ghosts</th>
          <th class="num">Filled</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let f of fieldsByDropOff">
          <td class="title">{{ f.label }}</td>
          <td class="num strong">{{ f.missingAmongStarted }}</td>
          <td class="num">{{ f.missingAmongGhosts }}</td>
          <td class="num meta">{{ f.filled }} / {{ report.stages.total }}</td>
        </tr>
      </tbody>
    </table>

    <h3>By signup week</h3>
    <p class="note">
      "Now listed" and "now top" are the accounts as they stand today, so they are
      true for every cohort. The median is only over crossings measured since the
      funnel started recording — the count beside it says how many that is.
    </p>
    <table>
      <thead>
        <tr>
          <th>Week</th>
          <th class="num">Signed up</th>
          <th class="num">Now listed</th>
          <th class="num">Now top</th>
          <th class="num">Median to listed</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let c of report.cohorts">
          <td class="title">{{ c.week | date: 'd MMM yyyy' }}</td>
          <td class="num">{{ c.signedUp }}</td>
          <td class="num">
            {{ c.nowListed }}
            <span class="meta">{{ percent(c.nowListed, c.signedUp) }}</span>
          </td>
          <td class="num">
            {{ c.nowTop }}
            <span class="meta">{{ percent(c.nowTop, c.signedUp) }}</span>
          </td>
          <td class="num">
            {{ duration(c.medianHoursToListed) }}
            <span class="meta" *ngIf="c.measuredListed">n={{ c.measuredListed }}</span>
            <span class="meta" *ngIf="!c.measuredListed">not measured</span>
          </td>
        </tr>
      </tbody>
    </table>
    <p class="empty" *ngIf="!report.cohorts.length">No signups in the last twelve weeks.</p>

    <h3>Did the nudges do anything</h3>
    <p class="note">
      A conversion is any field filled within a day of the nudge being shown, counted
      once per person — not per impression, so a banner shown on every launch cannot
      inflate its own success. A surface shown often and converting never is one to delete.
    </p>
    <table *ngIf="report.nudges.length">
      <thead>
        <tr>
          <th>Surface</th>
          <th class="num">People shown</th>
          <th class="num">Impressions</th>
          <th class="num">Converted</th>
          <th class="num">Rate</th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let n of report.nudges">
          <td class="title">{{ n.surface }}</td>
          <td class="num">{{ n.users }}</td>
          <td class="num meta">{{ n.shown }}</td>
          <td class="num">{{ n.convertedUsers }}</td>
          <td class="num strong">{{ conversionRate(n) }}</td>
        </tr>
      </tbody>
    </table>
    <p class="empty" *ngIf="!report.nudges.length">
      No nudges recorded yet. Impressions arrive once the app ships the reminders, or
      as soon as one is sent from the list below.
    </p>

    <div class="section-head">
      <h3>Stalled accounts</h3>
      <button
        mat-flat-button
        color="primary"
        (click)="nudgeAll()"
        [disabled]="nudgingAll || !report.stalledTotals.total"
      >
        {{ nudgingAll ? 'Working…' : 'Nudge all' }}
      </button>
    </div>
    <p class="note">
      Ghosts that have been around for more than a week, most recently active first —
      someone who signed up in March and opened the app this morning is still reachable.
      A nudge goes out as a push if their device is reachable, otherwise as an email
      from Sharon. <b>Nudge all</b> shows the counts and the copy before it sends, skips
      anyone reminded in the last 14 days or already reminded twice, and clears any push
      token the phone no longer answers to.
    </p>
    <p class="note totals">
      <b>{{ report.stalledTotals.total }}</b> stalled in all —
      <span class="pill reach-push">{{ report.stalledTotals.push }} push</span>
      <span class="pill reach-email">{{ report.stalledTotals.email }} email</span>
      <span class="pill reach-none">{{ report.stalledTotals.none }} unreachable</span>
      <span class="meta" *ngIf="report.stalledTotals.total > report.stalled.length">
        · showing the {{ report.stalled.length }} most recently active
      </span>
    </p>
    <table *ngIf="report.stalled.length">
      <thead>
        <tr>
          <th>Who</th>
          <th>Missing</th>
          <th>Reach</th>
          <th class="num">Signed up</th>
          <th class="num">Last seen</th>
          <th class="num">Reminded</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let s of report.stalled">
          <td>
            <div class="title">
              <app-person-link [uid]="s.id" [name]="s.name" (failed)="error = $event"></app-person-link>
            </div>
            <div class="meta">{{ s.email || 'no email' }}</div>
          </td>
          <td>
            <span class="pill">{{ missingSummary(s) }}</span>
            <div class="meta" *ngIf="!s.firstEditAt">never opened the editor</div>
          </td>
          <td>
            <span class="pill" [ngClass]="'reach-' + s.reach">{{ reachLabel(s.reach) }}</span>
          </td>
          <td class="num meta">{{ s.createdAt | date: 'd MMM yyyy' }}</td>
          <td class="num meta">{{ s.lastSeen | date: 'd MMM, HH:mm' }}</td>
          <td class="num">
            {{ s.remindersSent }}
            <span class="meta" *ngIf="s.lastReminderAt">last {{ s.lastReminderAt | date: 'd MMM' }}</span>
            <span class="meta" *ngIf="!s.remindersSent && s.nudgesShown">{{ s.nudgesShown }} in-app</span>
          </td>
          <td class="actions">
            <button
              mat-stroked-button
              [disabled]="busyId === s.id || nudgedIds.has(s.id) || s.reach === 'none'"
              (click)="nudge(s)"
            >
              {{ nudgedIds.has(s.id) ? 'Sent' : 'Nudge' }}
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <p class="empty" *ngIf="!report.stalled.length">
      No stalled ghosts — every account older than a week has enough on it to be shown.
    </p>
  </ng-container>
</div>
